import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const BlogSection = styled.section`
  padding-top: 8rem;
  background-color: #121212;
  text-align: center;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const BlogCard = styled.article`
  background-color: #1e1e1e;
  border-radius: 15px;
  padding: 0;
  text-align: left;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(59, 130, 246, 0.2);
    border-color: #3b82f6;
  }

  .image-container {
    height: 200px;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .icon {
      font-size: 4rem;
      color: white;
      opacity: 0.8;
    }
  }

  .content {
    padding: 2rem;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: #a0a0a0;
  }

  .category {
    background-color: #3b82f6;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .date {
    color: #a0a0a0;
  }

  .read-time {
    color: #a0a0a0;
  }

  h3 {
    color: #e0e0e0;
    font-size: 1.4rem;
    font-weight: 600;
    margin-bottom: 1rem;
    line-height: 1.3;

    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }

  p {
    color: #a0a0a0;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .author {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #333;
  }

  .author-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #60a5fa);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: white;
  }

  .author-info {
    .name {
      color: #e0e0e0;
      font-weight: 500;
      margin-bottom: 0.2rem;
    }
    
    .title {
      color: #a0a0a0;
      font-size: 0.9rem;
    }
  }

  .read-more {
    color: #3b82f6;
    font-weight: 500;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: color 0.3s ease;

    &:hover {
      color: #60a5fa;
    }

    &::after {
      content: '→';
    }
  }
`;

const FeaturedPost = styled.article`
  background-color: #1e1e1e;
  border-radius: 20px;
  padding: 0;
  margin-top: 3rem;
  overflow: hidden;
  border: 2px solid #3b82f6;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  min-height: 400px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .image-section {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    
    .icon {
      font-size: 6rem;
      color: white;
      opacity: 0.9;

      @media (max-width: 768px) {
        font-size: 4rem;
      }

      @media (max-width: 480px) {
        font-size: 3rem;
      }
    }
  }

  .content-section {
    padding: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 768px) {
      padding: 1.5rem;
    }

    @media (max-width: 480px) {
      padding: 1rem;
    }
  }

  .featured-badge {
    background-color: #3b82f6;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    width: fit-content;
    margin-bottom: 1rem;
  }

  h2 {
    color: #e0e0e0;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;

    @media (max-width: 480px) {
      font-size: 1.4rem;
    }
  }

  p {
    color: #a0a0a0;
    line-height: 1.6;
    margin-bottom: 2rem;
    font-size: 1.1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background-color: ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#a0a0a0'};
  border: 2px solid #3b82f6;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #3b82f6;
    color: white;
  }
`;


const Blog = () => {
  const featuredPost = {
    title: "The Future of AI in Business: Trends to Watch in 2024",
    excerpt: "Explore the latest developments in artificial intelligence and how they're reshaping business operations across industries. From autonomous systems to ethical AI frameworks.",
    icon: "🚀",
    slug: "future-of-ai-business-2024"
  };

  const blogPosts = [
    {
      title: "How NLP is Revolutionizing Customer Service",
      excerpt: "Discover how Natural Language Processing is transforming customer interactions and improving satisfaction rates across industries.",
      category: "NLP",
      readTime: "8 min read",
      icon: "🤖",
      slug: "nlp-revolutionizing-customer-service"
    },
    {
      title: "Building Ethical AI Systems: A Comprehensive Guide",
      excerpt: "Learn about the principles and practices needed to develop AI systems that are fair, transparent, and accountable.",
      category: "Ethics",
      readTime: "12 min read",
      icon: "⚖️",
      slug: "building-ethical-ai-systems-guide"
    },
    {
      title: "Automating Document Processing with AI",
      excerpt: "Step-by-step guide to implementing AI-powered document processing solutions that can handle various formats and extract key information.",
      category: "Automation",
      readTime: "10 min read",
      icon: "📄",
      slug: "automating-document-processing-ai"
    },
    {
      title: "Machine Learning Model Deployment Best Practices",
      excerpt: "Essential strategies for successfully deploying ML models in production environments, including monitoring and maintenance.",
      category: "Technical",
      readTime: "15 min read",
      icon: "⚙️",
      slug: "ml-model-deployment-best-practices"
    },
    {
      title: "The ROI of AI Implementation: Measuring Success",
      excerpt: "How to calculate and demonstrate the return on investment for AI projects, with real-world examples and metrics.",
      category: "Business",
      readTime: "7 min read",
      icon: "💰",
      slug: "roi-ai-implementation-measuring-success"
    },
    {
      title: "Data Privacy in AI: Compliance and Best Practices",
      excerpt: "Navigate the complex landscape of data privacy regulations while leveraging AI technologies effectively and responsibly.",
      category: "Privacy",
      readTime: "11 min read",
      icon: "🔒",
      slug: "data-privacy-ai-compliance-best-practices"
    }
  ];

  const categories = ['All', 'NLP', 'Ethics', 'Automation', 'Technical', 'Business', 'Privacy'];

  return (
    <>
      <BlogSection>
        <div className="container">
          <h2 className="section-subtitle">Blog</h2>
          <h1 className="section-title">AI Insights & Updates</h1>
          <p className="section-description" style={{ margin: '0 auto' }}>
            Stay ahead of the curve with the latest insights, trends, and best practices 
            in artificial intelligence and natural language processing.
          </p>

          <FeaturedPost>
            <div className="image-section">
              <span className="icon">{featuredPost.icon}</span>
            </div>
            <div className="content-section">
              <div className="featured-badge">Featured Post</div>
              <h2>{featuredPost.title}</h2>
              <p>{featuredPost.excerpt}</p>
              <Link to={`/blog/${featuredPost.slug}`} className="btn">
                Read Article
              </Link>
            </div>
          </FeaturedPost>
          
          <FilterSection>
            {categories.map((category, index) => (
              <FilterButton 
                key={index}
                $active={index === 0}
              >
                {category}
              </FilterButton>
            ))}
          </FilterSection>
          
          <BlogGrid>
            {blogPosts.map((post, index) => (
              <BlogCard key={index}>
                <div className="image-container">
                  <span className="icon">{post.icon}</span>
                </div>
                <div className="content">
                  <div className="meta">
                    <span className="category">{post.category}</span>
                    <span className="read-time">{post.readTime}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  
                  
                  <Link to={`/blog/${post.slug}`} className="read-more">
                    Read Article
                  </Link>
                </div>
              </BlogCard>
            ))}
          </BlogGrid>
        </div>
      </BlogSection>

    </>
  );
};

export default Blog;